import {
  Popover as ChakraPopover,
  Flex,
  PopoverContentProps,
  PopoverRootProps,
  PopoverTriggerProps,
} from '@chakra-ui/react'
import { ReactNode, useState } from 'react'
import { Text, XMarkIcon } from '.'
import { Portal, type PortalProps } from './providers'

export interface Props extends PopoverRootProps {
  children: ReactNode
  contentProps?: Omit<PopoverContentProps, 'children'>
  header?: ReactNode
  hideClose?: boolean
  onClose?: () => void
  onOpen?: () => void
  portal?: boolean | Omit<PortalProps, 'children'>
  showArrow?: boolean
  trigger: ReactNode
  triggerProps?: Omit<PopoverTriggerProps, 'children'>
}

export default function Popover({
  children,
  contentProps,
  header,
  hideClose,
  open,
  onClose,
  onOpenChange,
  onOpen,
  portal,
  positioning,
  showArrow,
  trigger,
  triggerProps,
  ...rest
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false)

  const _open = open ?? internalOpen

  return (
    <ChakraPopover.Root
      open={_open}
      positioning={{ ...positioning, placement: positioning?.placement || 'bottom-end' }}
      onOpenChange={p => {
        onOpenChange?.(p)
        if (open === undefined) setInternalOpen(p.open)
        else p.open ? onOpen?.() : onClose?.()
      }}
      {...rest}
    >
      <ChakraPopover.Trigger {...triggerProps}>{trigger}</ChakraPopover.Trigger>

      <Portal disabled={!portal} {...(typeof portal === 'object' ? portal : {})}>
        <ChakraPopover.Positioner>
          <ChakraPopover.Content maxWidth="90vw" width="inherit" {...contentProps}>
            {showArrow && <ChakraPopover.Arrow />}
            <Flex
              alignItems="center"
              justifyContent="space-between"
              p={header || !hideClose ? 4 : 0}
            >
              {header ? (
                typeof header === 'string' ? (
                  <Text fontWeight="semibold" opacity="0.6">
                    {header}
                  </Text>
                ) : (
                  header
                )
              ) : (
                <div />
              )}
              {!hideClose && (
                <ChakraPopover.CloseTrigger
                  asChild
                  cursor="pointer"
                  fontWeight="bold"
                  position="static"
                >
                  <XMarkIcon size="sm" />
                </ChakraPopover.CloseTrigger>
              )}
            </Flex>
            <ChakraPopover.Body overflow="auto" p={0}>
              {children}
            </ChakraPopover.Body>
          </ChakraPopover.Content>
        </ChakraPopover.Positioner>
      </Portal>
    </ChakraPopover.Root>
  )
}
